import { SelectionModel } from '@angular/cdk/collections';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Group, Vehicle, VehicleType } from 'lcmm-lib-js';
import { map, Observable, startWith, Subscription } from 'rxjs';
import { LcmmDataSource } from 'src/app/datasource/lcmm-data-source';
import { Sort } from 'src/app/datasource/page';
import { AuthService } from 'src/app/service/auth.service';
import { EnvConfigurationService } from 'src/app/service/env-config.service';
import {
  NetworkService,
  NetworkStateEvent,
} from 'src/app/service/network.service';
import { ResponsiveDesignService } from 'src/app/service/responsiveDesign.service';
import { UserService } from 'src/app/service/user.service';
import { VehicleQuery, VehicleService } from 'src/app/service/vehicle.service';
import { DateInputErrorStateMatcher } from 'src/app/utils/error-state-matcher';
import { CompareDialogComponent } from 'src/app/compare-dialog/compare-dialog.component';
import { MapHelper } from 'src/app/utils/markers/map-helper';
import { TranslateService } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-trip-vehicle-compare',
  standalone: false,
  templateUrl: './trip-vehicle-compare.component.html',
  styleUrl: './trip-vehicle-compare.component.scss',
})
export class TripVehicleCompareComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private readonly className = 'TripVehicleCompareComponent';

  private readonly largePageSize = 10000;

  // Table configuration
  public displayedColumns: string[] = ['_name'];

  public selectedDisplayedColumns: string[] = ['_name'];

  public initialSort: Sort = { property: '_name', order: 'asc' };

  // Pagination settings
  public pageSizeOptions: number[] = [5, 10];

  public pageSize = 10;

  private previousPageSize = -1;

  // Data sources
  public _originalVehiclesData: LcmmDataSource<Vehicle, VehicleQuery>;

  public availableVehiclesData = new MatTableDataSource<Vehicle>([]);

  public selectedVehiclesData = new MatTableDataSource<Vehicle>([]);

  private _allVehicles: Vehicle[] = [];

  private _dataSubscription: Subscription;

  // Selection models
  public vehicleSelection = new SelectionModel<Vehicle>(true, []);

  public selectedVehicleSelection = new SelectionModel<Vehicle>(true, []);

  public selectedVehicles: Vehicle[] = [];

  // Group and filter state
  public groups: Group[];

  public selectedGroup?: Group;

  public globalFilter = '';

  public groupSelected = false;

  public cutPositions = false;

  // Date fields
  public startDateValid = true;

  public endDateValid = true;

  public maxDate: Date = new Date();

  private startHour = undefined;

  private endHour = undefined;

  // Form controls
  public myControlGroup = new UntypedFormControl();

  public myControlStartDate = new UntypedFormControl(null);

  public myControlEndDate = new UntypedFormControl(null);

  public myControlStartTime = new UntypedFormControl(null);

  public myControlEndTime = new UntypedFormControl(null);

  public errorStateMatcher = new DateInputErrorStateMatcher();

  public filteredGroups: Observable<Group[]>;

  // Vehicle data
  public vehicleTypes: VehicleType[];

  // View children
  @ViewChild(MatPaginator) public paginator: MatPaginator;

  @ViewChild('availablePaginator') public availablePaginator: MatPaginator;

  @ViewChild('selectedPaginator') public selectedPaginator: MatPaginator;

  // Helper objects
  private mapHelper: MapHelper;

  private entireGroupSelected = false;

  constructor(
    public dialog: MatDialog,
    private datePipe: DatePipe,
    public vehicleService: VehicleService,
    private userService: UserService,
    private ts: TranslateService,
    public envService: EnvConfigurationService,
    private responsiveDesignService: ResponsiveDesignService,
    private cdr: ChangeDetectorRef,
    public authService: AuthService,
    public infoDialog: MatDialog,
    public networkService: NetworkService
  ) {
    this._originalVehiclesData = new LcmmDataSource<Vehicle, VehicleQuery>(
      'Vehicle',
      (request, query) => this.vehicleService.page(request, query),
      this.initialSort,
      { driver: undefined, group: undefined, global: undefined },
      this.networkService
    );

    this.mapHelper = new MapHelper(this.ts, this.datePipe);
  }

  ngOnInit(): void {
    this.networkService.register(this.className, this.nsCallback.bind(this));

    this.setupSubscriptions();
    this.connectToDataSource();
  }

  ngAfterViewInit(): void {
    this.resizePage(
      this.responsiveDesignService.register(
        this.className,
        this.resizePage.bind(this)
      )
    );

    setTimeout(() => this.updatePaginators(), 0);
  }

  ngOnDestroy(): void {
    this.networkService.deregister(this.className);
    this.responsiveDesignService.deregister(this.className);

    if (this._dataSubscription) {
      this._dataSubscription.unsubscribe();
    }
  }

  private setupSubscriptions(): void {
    this.vehicleService.vehicleTypes.subscribe((vehicleTypes) => {
      this.vehicleTypes = vehicleTypes;
    });

    this.userService.flattenedGroups.subscribe((groups) => {
      if (groups) {
        this.groups = groups;
        this.filteredGroups = this.myControlGroup.valueChanges.pipe(
          startWith(groups),
          map((value) => this.filterGroups(value))
        );
      }
    });

    this.myControlStartDate.statusChanges.subscribe((status) => {
      this.startDateValid = status === 'VALID';
    });

    this.myControlEndDate.statusChanges.subscribe((status) => {
      this.endDateValid = status === 'VALID';
    });

    this.myControlStartDate.valueChanges.subscribe(() => {
      this.validateDateRange();
      if (this.startDateValid && this.selectedGroup) {
        this.applyDateFilters();
      }
    });

    this.myControlEndDate.valueChanges.subscribe(() => {
      this.validateDateRange();
      if (this.endDateValid && this.selectedGroup) {
        this.applyDateFilters();
      }
    });
  }

  private validateDateRange(): void {
    const startDate = this.myControlStartDate.value;
    const endDate = this.myControlEndDate.value;

    if (startDate && endDate) {
      const startDateOnly = new Date(startDate);
      startDateOnly.setHours(0, 0, 0, 0);

      const endDateOnly = new Date(endDate);
      endDateOnly.setHours(0, 0, 0, 0);

      if (endDateOnly < startDateOnly) {
        this.myControlEndDate.setErrors({ endDateBeforeStart: true });
        this.endDateValid = false;
      }
    }
  }

  private connectToDataSource(): void {
    if (this._dataSubscription) {
      this._dataSubscription.unsubscribe();
    }

    this._originalVehiclesData.pageSize = this.largePageSize;

    this._dataSubscription = this._originalVehiclesData
      .connect()
      .subscribe((data: Vehicle[]) => {
        this._allVehicles = [...data];

        this.availableVehiclesData = new MatTableDataSource<Vehicle>([]);
        this.selectedVehiclesData = new MatTableDataSource<Vehicle>([]);

        if (this.selectedGroup) {
          this.applyDateFilters();
        }
      });
  }

  public isDateRangeValid(): boolean {
    if (!this.startDateValid || !this.endDateValid) {
      return false;
    }

    const startDate = this.myControlStartDate.value;
    const endDate = this.myControlEndDate.value;

    if (startDate && endDate) {
      const startDateOnly = new Date(startDate);
      startDateOnly.setHours(0, 0, 0, 0);

      const endDateOnly = new Date(endDate);
      endDateOnly.setHours(0, 0, 0, 0);

      return endDateOnly >= startDateOnly;
    }
    return true;
  }

  private applyDateFilters(): void {
    let filteredVehicles = [...this._allVehicles];

    let startDate = this.myControlStartDate.value
      ? new Date(this.myControlStartDate.value)
      : null;

    if (startDate) {
      startDate = new Date(
        Date.UTC(
          startDate.getFullYear(),
          startDate.getMonth(),
          startDate.getDate(),
          0,
          0,
          0,
          0
        )
      );

      filteredVehicles = filteredVehicles.filter((vehicle) => {
        if (
          vehicle.lastTimestamp === undefined ||
          vehicle.lastTimestamp === null
        ) {
          return false;
        }

        const vehicleDate = new Date(vehicle.lastTimestamp);

        return vehicleDate >= startDate;
      });
    }
    const selectedIds = new Set(
      this.selectedVehiclesData.data.map((v) => v.id)
    );
    this.availableVehiclesData.data = filteredVehicles.filter(
      (v) => !selectedIds.has(v.id)
    );

    this.updatePaginators();
  }

  private nsCallback(eventType: NetworkStateEvent): void {
    switch (eventType) {
      case NetworkStateEvent.ONLINE:
        this._originalVehiclesData.fetch();
        break;
      case NetworkStateEvent.CLIENTERROR:
      case NetworkStateEvent.SERVERERROR:
        if (this.paginator) {
          this.paginator.pageIndex -= 1;
        }
        break;
      default:
        break;
    }
  }

  private updatePaginators(): void {
    this.cdr.detectChanges();

    setTimeout(() => {
      if (
        this.availablePaginator &&
        this.availableVehiclesData.data.length > 0
      ) {
        this.availableVehiclesData.paginator = this.availablePaginator;
        this.availablePaginator._changePageSize(
          this.availablePaginator.pageSize || this.pageSize
        );
        this.availablePaginator.firstPage();
      }

      if (this.selectedPaginator && this.selectedVehiclesData.data.length > 0) {
        this.selectedVehiclesData.paginator = this.selectedPaginator;
        this.selectedPaginator._changePageSize(
          this.selectedPaginator.pageSize || this.pageSize
        );
        this.selectedPaginator.firstPage();
      }
    });
  }

  private resizePage(pageSize: number): void {
    this.pageSize = pageSize;

    if (this._originalVehiclesData.pageSize !== this.largePageSize) {
      this._originalVehiclesData.fetch(0, this.largePageSize);
    }
  }

  public resetPage(pageSize: number): void {
    this.pageSize = pageSize;
    const needsReset =
      this.previousPageSize > 0 && this.previousPageSize !== pageSize;

    if (this.availablePaginator) {
      this.availablePaginator.pageSize = pageSize;
      if (needsReset) {
        this.availablePaginator.firstPage();
      }
    }

    if (this.selectedPaginator) {
      this.selectedPaginator.pageSize = pageSize;
      if (needsReset) {
        this.selectedPaginator.firstPage();
      }
    }

    this.previousPageSize = pageSize;
    this.updatePaginators();
  }

  public fetch(): void {
    this._originalVehiclesData.fetch(0, this.largePageSize);
  }

  public onAvailablePaginateChange(event: PageEvent): void {
    if (this.availablePaginator) {
      this.availablePaginator.pageSize = event.pageSize;
    }
  }

  public onSelectedPaginateChange(event: PageEvent): void {
    if (this.selectedPaginator) {
      this.selectedPaginator.pageSize = event.pageSize;
    }
  }

  private filterGroups(value: unknown): Group[] {
    if (typeof value === 'string') {
      const filterValue = value.toLowerCase();
      return this.groups.filter((group) => {
        if (group.groupName) {
          return group.groupName.toLowerCase().includes(filterValue);
        }
        return true;
      });
    }
    return this.groups;
  }

  public displayGroup(group: Group): string {
    return group && group.groupName ? group.groupName : '';
  }

  public selectGroup(group: Group): void {
    this.selectedGroup = group;
    this.queryByFilter();
    this.groupSelected = true;
    this.updatePaginators();
  }

  public unselectGroup(): void {
    this.selectedGroup = undefined;
    this.myControlGroup.reset();
    this.queryByFilter(false);
    this.availableVehiclesData.data = [];
    this.selectedVehiclesData.data = [];
    this.groupSelected = false;
  }

  public resetStartDate(): void {
    this.myControlStartDate.setValue(null);
    this.startDateValid = true;
    if (this.selectedGroup) {
      this.applyDateFilters();
    }
  }

  public resetEndDate(): void {
    this.myControlEndDate.setValue(null);
    this.endDateValid = true;
    if (this.selectedGroup) {
      this.applyDateFilters();
    }
  }

  public resetStartTime(): void {
    this.myControlStartTime.setValue(null);
    this.startHour = undefined;
  }

  public resetEndTime(): void {
    this.myControlEndTime.setValue(null);
    this.endHour = undefined;
  }

  public queryByFilter(preserveSelected = true): void {
    const currentSelectedVehicles = preserveSelected
      ? [...this.selectedVehiclesData.data]
      : [];

    this._originalVehiclesData.queryBy({
      group: this.myControlGroup.value,
      global: this.globalFilter,
    });

    if (!this.selectedGroup) {
      this.availableVehiclesData.data = [];
    }

    this._originalVehiclesData.fetch(0, this.largePageSize);

    setTimeout(() => {
      if (preserveSelected) {
        const selectedVehiclesToRestore = currentSelectedVehicles;

        this.applyDateFilters();

        this.selectedVehiclesData.data = selectedVehiclesToRestore;

        const selectedIds = new Set(selectedVehiclesToRestore.map((v) => v.id));
        this.availableVehiclesData.data =
          this.availableVehiclesData.data.filter((v) => !selectedIds.has(v.id));
      } else {
        this.applyDateFilters();
      }

      this.updatePaginators();
    }, 300);
  }

  private refreshAvailableVehicles(): void {
    this.applyDateFilters();
  }

  public moveToSelected(): void {
    const vehiclesToAdd = this.vehicleSelection.selected;
    const currentSelected = [...this.selectedVehiclesData.data];

    vehiclesToAdd.forEach((v) => {
      if (!currentSelected.some((cv) => cv.id === v.id)) {
        currentSelected.push({ ...v });
      }
    });

    this.selectedVehiclesData.data = currentSelected;
    this.entireGroupSelected =
      this.selectedVehiclesData.data.length === this._allVehicles.length;
    this.vehicleSelection.clear();
    this.refreshAvailableVehicles();
    this.updatePaginators();
  }

  public moveToAvailable(): void {
    const vehiclesToRemoveIds = this.selectedVehicleSelection.selected.map(
      (v) => v.id
    );

    this.selectedVehiclesData.data = this.selectedVehiclesData.data.filter(
      (v) => !vehiclesToRemoveIds.includes(v.id)
    );
    this.entireGroupSelected = false;
    this.selectedVehicleSelection.clear();
    this.refreshAvailableVehicles();
    this.updatePaginators();
  }

  public moveAllToSelected(): void {
    // Get only filtered vehicles to move all to selected
    const filteredVehiclesData = this.availableVehiclesData.data;
    const selectedIds = new Set(
      this.selectedVehiclesData.data.map((v) => v.id)
    );

    const newSelected = filteredVehiclesData.filter(
      (v) => !selectedIds.has(v.id)
    );

    this.selectedVehiclesData.data = [
      ...this.selectedVehiclesData.data,
      ...newSelected.map((v) => ({ ...v })),
    ];

    this.entireGroupSelected =
      this.selectedVehiclesData.data.length === this._allVehicles.length;

    this.vehicleSelection.clear();
    this.refreshAvailableVehicles();
    this.updatePaginators();
  }

  public moveAllToAvailable(): void {
    this.selectedVehiclesData.data = [];
    this.entireGroupSelected = false;
    this.selectedVehicleSelection.clear();
    this.refreshAvailableVehicles();
    this.updatePaginators();
  }

  public toggleVehicleSelection(vehicle: Vehicle): void {
    this.vehicleSelection.toggle(vehicle);
    this.cdr.detectChanges();
  }

  public toggleSelectedVehicleSelection(vehicle: Vehicle): void {
    this.selectedVehicleSelection.toggle(vehicle);
  }

  public compare(): void {
    const startDate = this.myControlStartDate.value
      ? new Date(this.myControlStartDate.value)
      : null;
    const endDate = this.myControlEndDate.value
      ? new Date(this.myControlEndDate.value)
      : null;

    if (startDate) startDate.setHours(0, 0, 0, 0);
    if (endDate) endDate.setHours(23, 59, 59, 999);

    if (this.myControlStartTime.value && this.myControlStartTime.value._d) {
      const localDate = new Date(this.myControlStartTime.value._d);
      const hours = localDate.getUTCHours().toString().padStart(2, '0');
      const minutes = localDate.getUTCMinutes().toString().padStart(2, '0');
      this.startHour = `${hours}:${minutes}`;
    }

    if (this.myControlEndTime.value && this.myControlEndTime.value._d) {
      const localDate = new Date(this.myControlEndTime.value._d);
      const hours = localDate.getUTCHours().toString().padStart(2, '0');
      const minutes = localDate.getUTCMinutes().toString().padStart(2, '0');
      this.endHour = `${hours}:${minutes}`;
    }

    CompareDialogComponent.open(this.infoDialog, {
      groupIdentifier: this.selectedGroup.groupIdentifier,
      startDate,
      endDate,
      hourFrom: this.startHour,
      hourTo: this.endHour,
      bounds: this.mapHelper.getSelectedSectionBounds(),
      cutToBounds: this.cutPositions,
      vehicleId: this.selectedVehiclesData.data.map((v) => v.id),
      vehicleName: this.selectedVehiclesData.data.map((v) => v.name),
      isEntireGroupSelected: this.entireGroupSelected,
      mode: 'vehicleComparison',
    });
  }
}
